import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Payment as PaymentIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../hooks/useApi';
import { apiService } from '../services/api';
import { formatDate, formatCurrency } from '../utils/helpers';

interface Commission {
  _id: string;
  installationId: string;
  technicianId: string;
  technicianName: string;
  clientName: string;
  amount: number;
  isPaid: boolean;
  createdAt: string;
  paidAt?: string;
  notes?: string;
}

interface TechnicianPerformance {
  technicianId: string;
  technicianName: string;
  totalInstallations: number;
  successfulInstallations: number;
  failedInstallations: number;
  successRate: number;
  totalCommissions: number;
  paidCommissions: number;
  pendingCommissions: number;
  averageCommission: number;
  lastInstallation: string;
}

interface ReportFilters {
  startDate: string;
  endDate: string;
  technicianId: string;
  status: 'all' | 'paid' | 'pending';
}

export const TechnicianReports: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payCommissionDialog, setPayCommissionDialog] = useState({
    open: false,
    commission: null as Commission | null,
  });
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0],
    technicianId: '',
    status: 'all',
  });
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'warning' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  // Load installations and commissions
  // Note: Don't send 'status' filter to backend - it expects 'installed'/'failed'/'partial',
  // but our filter uses 'all'/'paid'/'pending' for commission filtering (done client-side)
  const {
    data: installationsData,
    loading: installationsLoading,
    error: installationsError,
    execute: fetchInstallations,
  } = useApi(() => apiService.getInstallations(1, 100, {
    technicianId: filters.technicianId || undefined,
    startDate: filters.startDate,
    endDate: filters.endDate,
  }));

  const {
    data: techniciansData,
    loading: techniciansLoading,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  const { execute: payCommission, loading: payingCommission } = useAsyncOperation();

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchInstallations();
    fetchTechnicians();
  }, [filters]);

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  // Process data for reports
  const { commissions, technicianPerformance } = useMemo(() => {
    // useApi ya desempaqueta un nivel (response.data): installationsData = { installations, pagination }
    const installations = (installationsData as any)?.installations || [];
    const technicians = (techniciansData as any)?.users || [];

    // Extract commissions from installations
    const commissionsData: Commission[] = installations
      .filter((inst: any) => inst.commission)
      .map((inst: any) => {
        // technicianId puede venir populado (objeto {_id, name}) o como string crudo.
        const techId = String(inst.technicianId?._id || inst.technicianId || '');
        const technician = technicians.find((t: any) => String(t._id) === techId);
        return {
          _id: inst._id,
          installationId: inst._id,
          technicianId: techId,
          technicianName: technician?.name || inst.technicianId?.name || inst.technicianName || 'Técnico no encontrado',
          clientName: typeof inst.clientId === 'object' ? inst.clientId.name : 'Cliente',
          amount: inst.commission.amount,
          isPaid: inst.commission.isPaid,
          createdAt: inst.installationDate,
          paidAt: inst.commission.paidDate || inst.commission.paidAt,
          notes: inst.commission.notes,
        };
      });

    // Calculate technician performance
    const performanceData: TechnicianPerformance[] = technicians
      .filter((tech: any) => tech.role === 'technician')
      .map((tech: any) => {
        const techInstallations = installations.filter((inst: any) => String(inst.technicianId?._id || inst.technicianId || '') === String(tech._id));
        const successfulInstallations = techInstallations.filter((inst: any) => inst.status === 'installed');
        const failedInstallations = techInstallations.filter((inst: any) => inst.status === 'failed');
        const techCommissions = commissionsData.filter(c => c.technicianId === tech._id);
        const paidCommissions = techCommissions.filter(c => c.isPaid);
        const pendingCommissions = techCommissions.filter(c => !c.isPaid);

        return {
          technicianId: tech._id,
          technicianName: tech.name,
          totalInstallations: techInstallations.length,
          successfulInstallations: successfulInstallations.length,
          failedInstallations: failedInstallations.length,
          successRate: techInstallations.length > 0 ? (successfulInstallations.length / techInstallations.length) * 100 : 0,
          totalCommissions: techCommissions.reduce((sum, c) => sum + c.amount, 0),
          paidCommissions: paidCommissions.reduce((sum, c) => sum + c.amount, 0),
          pendingCommissions: pendingCommissions.reduce((sum, c) => sum + c.amount, 0),
          averageCommission: techCommissions.length > 0 ? techCommissions.reduce((sum, c) => sum + c.amount, 0) / techCommissions.length : 0,
          lastInstallation: techInstallations.length > 0
            ? new Date(Math.max(...techInstallations.map((inst: any) => new Date(inst.installationDate).getTime()))).toISOString()
            : 'Nunca',
        };
      });

    return { commissions: commissionsData, technicianPerformance: performanceData };
  }, [installationsData, techniciansData]);

  // Filter commissions based on current filters
  const filteredCommissions = useMemo(() => {
    return commissions.filter(commission => {
      if (filters.technicianId && commission.technicianId !== filters.technicianId) return false;
      if (filters.status === 'paid' && !commission.isPaid) return false;
      if (filters.status === 'pending' && commission.isPaid) return false;

      const commissionDate = new Date(commission.createdAt);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);

      return commissionDate >= startDate && commissionDate <= endDate;
    });
  }, [commissions, filters]);

  // La tabla de abajo lista una fila POR INSTALACIÓN, así que el mismo técnico se
  // repite decenas de veces y no se ve el total de nadie. Esto lo agrupa: un técnico,
  // sus instalaciones y lo que se le debe.
  const comisionesPorTecnico = useMemo(() => {
    const mapa = new Map<string, {
      technicianId: string; technicianName: string;
      installations: number; total: number; paid: number; pending: number; pendingCount: number;
    }>();
    for (const c of filteredCommissions) {
      const clave = c.technicianId || c.technicianName;
      if (!mapa.has(clave)) {
        mapa.set(clave, {
          technicianId: c.technicianId, technicianName: c.technicianName,
          installations: 0, total: 0, paid: 0, pending: 0, pendingCount: 0,
        });
      }
      const t = mapa.get(clave)!;
      t.installations += 1;
      t.total += c.amount || 0;
      if (c.isPaid) t.paid += c.amount || 0;
      else { t.pending += c.amount || 0; t.pendingCount += 1; }
    }
    // Array.from y no [...mapa.values()]: el proyecto compila a ES5 y el spread de
    // un iterador falla ahí (TS2802).
    return Array.from(mapa.values()).sort((a, b) => b.pending - a.pending || b.total - a.total);
  }, [filteredCommissions]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePayCommission = async (commission: Commission) => {
    try {
      const result = await payCommission(() =>
        apiService.markInstallationPaid(commission.installationId, `PAID-${Date.now()}`)
      );

      if (result) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Comisión marcada como pagada',
        });
        setPayCommissionDialog({ open: false, commission: null });
        fetchInstallations();
      }
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Error procesando pago de comisión',
      });
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
    const headers = Object.keys(data[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getTechnicianColor = (technicianId: string) => {
    const colors = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B'];
    const index = technicianId.length % colors.length;
    return colors[index];
  };

  return (
    <Box>
      {/* Alert */}
      {alert.show && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Reportes y Comisiones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona pagos de comisiones y analiza rendimiento de técnicos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToCSV(filteredCommissions, 'comisiones')}
            disabled={filteredCommissions.length === 0}
          >
            Exportar Comisiones
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToCSV(technicianPerformance, 'rendimiento_tecnicos')}
            disabled={technicianPerformance.length === 0}
          >
            Exportar Rendimiento
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {formatCurrency(commissions.reduce((sum, c) => sum + c.amount, 0))}
              </Typography>
              <Typography variant="caption">Total Comisiones</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {formatCurrency(commissions.filter(c => c.isPaid).reduce((sum, c) => sum + c.amount, 0))}
              </Typography>
              <Typography variant="caption">Comisiones Pagadas</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {formatCurrency(commissions.filter(c => !c.isPaid).reduce((sum, c) => sum + c.amount, 0))}
              </Typography>
              <Typography variant="caption">Comisiones Pendientes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                {commissions.filter(c => !c.isPaid).length}
              </Typography>
              <Typography variant="caption">Pagos Pendientes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Filtros
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Fecha Inicio"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Fecha Fin"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Técnico</InputLabel>
              <Select
                value={filters.technicianId}
                label="Técnico"
                onChange={(e) => setFilters({ ...filters, technicianId: e.target.value })}
              >
                <MenuItem value="">Todos los técnicos</MenuItem>
                {((techniciansData as any)?.users || [])
                  .filter((tech: any) => tech.role === 'technician')
                  .map((tech: any) => (
                    <MenuItem key={tech._id} value={tech._id}>
                      {tech.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="paid">Pagadas</MenuItem>
                <MenuItem value="pending">Pendientes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label="Comisiones"
            icon={<PaymentIcon />}
            iconPosition="start"
          />
          <Tab
            label="Rendimiento"
            icon={<TrendingUpIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Gestión de Comisiones
          </Typography>

          {/* Resumen por técnico: la tabla de abajo repite al mismo técnico en cada
              instalación, así que aquí se ve de un vistazo cuánto lleva cada uno. */}
          {comisionesPorTecnico.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
              {comisionesPorTecnico.map((t) => (
                <Paper
                  key={t.technicianId || t.technicianName}
                  variant="outlined"
                  sx={{ p: 1.5, flex: '1 1 240px', minWidth: 0, borderLeft: `4px solid ${getTechnicianColor(t.technicianId)}` }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ bgcolor: getTechnicianColor(t.technicianId), width: 32, height: 32 }}>
                      {(t.technicianName || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{t.technicianName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.installations} {t.installations === 1 ? 'instalación' : 'instalaciones'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Pendiente</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: t.pending > 0 ? 'warning.main' : 'text.secondary' }}>
                        {formatCurrency(t.pending)}
                      </Typography>
                      {t.pendingCount > 0 && (
                        <Typography variant="caption" color="text.secondary">{t.pendingCount} sin pagar</Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Pagado</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(t.paid)}</Typography>
                      <Typography variant="caption" color="text.secondary">total {formatCurrency(t.total)}</Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Detalle por instalación
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Técnico</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Monto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCommissions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((commission) => (
                    <TableRow key={commission._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: getTechnicianColor(commission.technicianId), width: 32, height: 32 }}>
                            {commission.technicianName.charAt(0).toUpperCase()}
                          </Avatar>
                          {commission.technicianName}
                        </Box>
                      </TableCell>
                      <TableCell>{commission.clientName}</TableCell>
                      <TableCell>{formatCurrency(commission.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={commission.isPaid ? 'Pagada' : 'Pendiente'}
                          color={commission.isPaid ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(commission.createdAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Ver detalles">
                            <IconButton size="small" color="primary">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {!commission.isPaid && (
                            <Tooltip title="Marcar como pagada">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setPayCommissionDialog({ open: true, commission })}
                              >
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredCommissions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {currentTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Rendimiento de Técnicos
          </Typography>
          <Grid container spacing={3}>
            {technicianPerformance.map((tech) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={tech.technicianId}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Avatar sx={{ bgcolor: getTechnicianColor(tech.technicianId) }}>
                        {tech.technicianName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tech.technicianName}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                            {tech.totalInstallations}
                          </Typography>
                          <Typography variant="caption">Instalaciones</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                            {tech.successRate.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption">Éxito</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tech.totalCommissions)}
                          </Typography>
                          <Typography variant="caption">Total Comisiones</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main" sx={{ fontWeight: 600 }}>
                            {formatCurrency(tech.pendingCommissions)}
                          </Typography>
                          <Typography variant="caption">Pendientes</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Pay Commission Dialog */}
      <Dialog
        open={payCommissionDialog.open}
        onClose={() => setPayCommissionDialog({ open: false, commission: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Pago de Comisión</DialogTitle>
        <DialogContent>
          {payCommissionDialog.commission && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ¿Confirmas que deseas marcar como pagada la comisión de{' '}
                <strong>{formatCurrency(payCommissionDialog.commission.amount)}</strong> para{' '}
                <strong>{payCommissionDialog.commission.technicianName}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cliente: {payCommissionDialog.commission.clientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fecha: {formatDate(payCommissionDialog.commission.createdAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPayCommissionDialog({ open: false, commission: null })}
            disabled={payingCommission}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => payCommissionDialog.commission && handlePayCommission(payCommissionDialog.commission)}
            disabled={payingCommission}
            startIcon={payingCommission ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {payingCommission ? 'Procesando...' : 'Confirmar Pago'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianReports;